// src/components/pwa/InstallPrompt.tsx
'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { X, Share, PlusSquare } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { isCtaCriticalRoute } from '@/lib/pwa-prompt-routes';

const DISMISS_KEY = 'aksademia_pwa_dismissed';
const DISMISS_DAYS = 14;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
}

function isIos(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIosDevice = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIosDevice && isSafari;
}

// Dismissal used to live in localStorage alone, and the write was wrapped in a
// silent catch. When that write fails — private windows, blocked site data, Safari
// storage partitioning inside a standalone PWA — the dismissal evaporated and the
// banner came back on every single page. Closing it did nothing.
//
// Fall back through sessionStorage to a module-level flag, so a dismissal always
// sticks for at least the current session no matter what storage is available.
let dismissedInMemory = false;

// Reading `window.sessionStorage` THROWS in Chrome when site data is blocked —
// "Failed to read the 'sessionStorage' property from 'Window': Access is denied for
// this document" — so the property access has to sit inside the try, not just the
// getItem/setItem call. Guarding only the call left the dereference unprotected and
// crashed the tree on /login for mobile users with third-party data blocked.
function getStore(kind: 'sessionStorage' | 'localStorage'): Storage | undefined {
  try {
    return window[kind];
  } catch {
    return undefined;
  }
}

function readTimestamp(store: Storage | undefined): number | null {
  try {
    const raw = store?.getItem(DISMISS_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function isDismissalActive(): boolean {
  if (dismissedInMemory) return true;

  // A session-scoped dismissal is unconditional: the user closed it, so it stays
  // closed until they open a new tab.
  if (readTimestamp(getStore('sessionStorage'))) return true;

  const dismissedAt = readTimestamp(getStore('localStorage'));
  if (!dismissedAt) return false;
  const elapsedDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
  return elapsedDays < DISMISS_DAYS;
}

function persistDismissal(): void {
  const stamp = String(Date.now());
  dismissedInMemory = true;
  for (const kind of ['sessionStorage', 'localStorage'] as const) {
    try {
      getStore(kind)?.setItem(DISMISS_KEY, stamp);
    } catch {
      // Storage blocked. The in-memory flag still holds for this session.
    }
  }
}

/**
 * OFF by default — set NEXT_PUBLIC_PWA_INSTALL_PROMPT_ENABLED=true to bring it back.
 *
 * Installing is still possible (the manifest is untouched, Add to Home Screen works); we just
 * stop *pitching* it. An installed iOS PWA gets its own storage jar, separate from Safari's,
 * and takes a different sign-in path — shouldUseRedirect() returns true in standalone, so it
 * runs signInWithRedirect where Safari runs signInWithPopup. Two divergences from the surface
 * that actually gets tested, and the redirect path is the one our own should-use-redirect.ts
 * documents as fragile. Users following this prompt landed in a login loop no amount of
 * retrying could clear.
 *
 * Re-enable once a fresh PWA install has been verified end to end: sign in, sit a full test,
 * submit. That is the bar — a session dropping mid-exam is far worse than never installing.
 */
const INSTALL_PROMPT_ENABLED = process.env.NEXT_PUBLIC_PWA_INSTALL_PROMPT_ENABLED === 'true';

export function InstallPrompt() {
  const t = useTranslations('pwa');
  const pathname = usePathname();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosBanner, setShowIosBanner] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // Production only. In dev the SW caches hashed JS chunks and then keeps serving
    // them after the code changes — it survives a `.next` wipe AND a dev-server
    // restart, so an edit silently doesn't apply and the stale bundle throws
    // "module factory is not available" from a module you already deleted. That
    // reads exactly like a code bug and isn't one. Actively unregister any SW a
    // previous dev session left behind, and drop its caches.
    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister());
      }).catch(() => {});
      caches?.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
      return;
    }

    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Registration failures are non-fatal — the app still works without a SW.
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStandalone() || isDismissalActive()) return;

    if (isIos()) {
      setShowIosBanner(true);
      setVisible(true);
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleDismiss = () => {
    persistDismissal();
    setVisible(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      persistDismissal();
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  // Never over a sign-in / onboarding CTA — the banner sits on top of it on phones.
  if (!INSTALL_PROMPT_ENABLED || !visible || isCtaCriticalRoute(pathname)) return null;

  return (
    <div className="fixed inset-x-0 bottom-20 lg:bottom-4 z-30 flex justify-center px-4">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white shadow-lg p-4 flex items-start gap-3">
        <Image
          src="/icon-192.png"
          alt=""
          width={40}
          height={40}
          className="rounded-lg shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">{t('title')}</p>
          <p className="text-xs text-slate-600 mt-0.5">{t('description')}</p>

          {showIosBanner ? (
            <p className="text-xs text-slate-500 mt-2 inline-flex flex-wrap items-center gap-1">
              {t('iosInstruction')}
              <Share className="w-3.5 h-3.5 inline text-slate-500" aria-hidden="true" />
              <PlusSquare className="w-3.5 h-3.5 inline text-slate-500" aria-hidden="true" />
            </p>
          ) : (
            <button
              type="button"
              onClick={handleInstall}
              className="mt-2 inline-flex items-center rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700"
            >
              {t('installButton')}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('dismiss')}
          className="text-slate-400 hover:text-slate-600 shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
